import React from 'react'

interface RichTextProps {
  defaultValue: string,
  onChange: any
}

interface RichTextState {
  value?: string,
}

const formatTools = ['bold', 'italic', 'underline', 'strikethrough', 'insertUnorderedList', 'insertOrderedList', 'outdent', 'indent']

const insertToken = (node,offset) => {
  const parent = node.parentNode
  const token = document.createElement("span")
  if (node.nodeType === Node.TEXT_NODE) {
    const splitted = node.splitText(offset)
    parent.insertBefore(token,splitted)
  } else {
    parent.insertBefore(token,node)
  }
  return token
}

export class RichTextEditor extends React.Component<{}, RichTextProps, RichTextState> {

  input
  container
  commands = []

  constructor(props) {
    super(props)
    this.state = {value: this.props.defaultValue}
  }

  triggerScrollForPanel = (d) => {
    requestAnimationFrame((time)=> {
        if (time>d+250) return
        window.dispatchEvent(new Event('scroll'))
        this.triggerScrollForPanel(d)
      }
    )
  }

  focused = () => {
    this.setState({focused: 2})
    this.triggerScrollForPanel(Date.now())
  }

  blurred = () => {
    if (this.state.focused !== 1) {
      this.setState({focused: 0})
      this.triggerScrollForPanel(Date.now())
    }
  }

  execCommandMouseUp = (command) => {
    if (this.startSelectionToken) {
      window.getSelection().removeAllRanges()
      const range = document.createRange()
      range.setStart(this.startSelectionToken.nextSibling,0)
      const end = this.endSelectionToken.nextSibling
      this.startSelectionToken.remove()
      this.endSelectionToken.remove()
      this.startSelectionToken = null
      this.endSelectionToken = null
      range.setEnd(end,0)
      window.getSelection().addRange(range)
    } else {
      this.input.focus()
    }
    this.commands.forEach( c => {
      const sanitizedCommand = (c.indexOf("List")>=0 && document.queryCommandState(c)) ? 'outdent' : c // FIX CHROME HANGS when list is called and already active
      document.execCommand(sanitizedCommand)
    })
    this.props.onChange(this.input.innerHTML)
    this.toggleButtonsState()
  }

  execCommand = (e, command) => {
    if (window.getSelection().rangeCount) {
      this.commands = window.getSelection().getRangeAt(0).collapsed ? this.commands.concat(command) : [command]
      if (window.getSelection().getRangeAt(0).startContainer === this.input) { // firefox ctrl+a bug
        this.startSelectionToken = insertToken(this.input.firstChild,0)
        this.endSelectionToken = insertToken(this.input.lastChild,this.input.lastChild.length)
      } else {
        this.startSelectionToken = insertToken(window.getSelection().getRangeAt(0).startContainer,window.getSelection().getRangeAt(0).startOffset)
        this.endSelectionToken = insertToken(window.getSelection().getRangeAt(0).endContainer,window.getSelection().getRangeAt(0).endOffset)
      }
    }
    this.setState({focused: 1})
  }

  handleKeyUp = (e) => {
    this.toggleButtonsState()
    this.commands = []
    this.onChange(this.input.innerHTML)
  }

  onChange = (innerHTML) => {
    this.props.onChange(innerHTML)
    this.commands = []
    window.dispatchEvent(new Event('resize'))
  }

  handlePasteDrop = (e) => {
    setTimeout(() => {
      this.pasted(e)
      this.onChange(this.input.innerHTML)
    }, 1)
  }
  handleClick = (e) => {
    this.toggleButtonsState()
    this.commands = []
  }

  ToolButton = ({tool}) =>
    <button
      onMouseDown={(e) => this.execCommand(e, tool)}
      onMouseUp={(e) => this.execCommandMouseUp(tool)}
      tabIndex={-1}
      className={'exec-command rich-text-button only-icon ' + (this.state[tool + 'Activated'] ? 'activated' : '')}
      type="button"
    >
      <span className={'icon icon-' + tool}/>
    </button>

  render() {
    const {focused, hover} = this.state
    const ToolButton = this.ToolButton
    return (
      <div
        data-drag={false}
        onFocus={this.focused}
        onBlur={this.blurred}
        className={'rich-text-container' + (focused || hover ? ' focused' : '')}
        ref={e => this.container = e}
      >
        <div ref={(el) => this.input = el} onClick={this.handleClick} onKeyUp={this.handleKeyUp}
             onBlur={this.props.onBlur}
             onPaste={this.handlePasteDrop}
             onDrop={this.handlePasteDrop}
             contentEditable={true} tabIndex={2}
             className="rich-text-editor" dangerouslySetInnerHTML={{__html: this.state.value}}/>
        <div className="rich-text-tools">
          {formatTools.slice(0, 4).map(t => <ToolButton key={t} tool={t}/>)}
          <span className="separator"/>
          {formatTools.slice(4, 6).map(t => <ToolButton key={t} tool={t}/>)}
          <span className="separator"/>
          {formatTools.slice(6, 8).map(t => <ToolButton key={t} tool={t}/>)}
        </div>
      </div>
    )
  }


  setSelectionRange = (input, selectionStart, selectionEnd = 0) => {
    let range = document.createRange()
    let sel = window.getSelection()
    range.setStart(input, selectionStart)
    if (selectionEnd) {
      range.setEnd(input, selectionEnd)
    } else {
      range.collapse(true)
    }
    sel.removeAllRanges()
    sel.addRange(range)
  }

  pasted = (e) => {
    const editor = this.input

    Array.prototype.slice.call(editor.querySelectorAll('.Apple-interchange-newline')).forEach(el => el.remove())
    Array.prototype.slice.call(editor.querySelectorAll('iframe')).forEach(el => el.remove())

    const newSpace = document.createTextNode('\u00A0')
    if (window.getSelection().rangeCount) {
      const container = window.getSelection().getRangeAt(0).startContainer
      if (container.nodeType === 3) {
        container.parentNode.insertBefore(newSpace, container.nextSibling)
      } else {
        container.appendChild(newSpace)
      }
      if (newSpace.previousSibling && newSpace.previousSibling.textContent === '\u00A0') {
        newSpace.previousSibling.remove()
      }
      this.setSelectionRange(newSpace, 0)
    }

    const acceptedTags = ['b', 'ul', 'li', 'br', 'div', 'i', 'p', 'strike', 'strong', 'span', 'u']
    const clean = (children) => {
      Array.prototype.slice.call(children).forEach(el => {
        try {
          if (el.nodeType === 3)
            return

          if (!el.tagName) {
            el.remove()
            return
          }
          let tagName = el.tagName.toLowerCase()
          const style = getComputedStyle(el)
          const rect = el.getBoundingClientRect()
          const isHidden = style.display === 'none' || rect.width === 0 || rect.height === 0
          if (isHidden && tagName !== 'br') {
            el.remove()
          } else if (acceptedTags.indexOf(tagName) >= 0) {
            clean(el.childNodes)
            el.removeAttribute('class')
            if (el.getAttribute('style')) {
              const {fontWeight: usingFontWeight, fontStyle} = el.style
              const intFontWeight = parseInt(window.getComputedStyle(el).fontWeight)
              const fontWeight = !usingFontWeight ? undefined : intFontWeight <= 400 ? 400 : 600
              el.removeAttribute('style')
              Object.assign(el.style, {fontWeight, fontStyle})
            }
          } else {
            clean(el.childNodes)
            if (el.hasChildNodes()) {
              const newTagName = style.display === 'inline' ? 'span' : 'div'
              const newEl = document.createElement(newTagName)
              Array.prototype.slice.call(el.childNodes).forEach(child => newEl.appendChild(child))
              el.parentNode.replaceChild(newEl, el)
            } else {
              el.remove()
            }
          }
        } catch (e) {
          if (el && el.remove)
            el.remove()
        }
      })
    }
    clean(editor.childNodes)
  }

  toggleButtonsState = () => {
    this.setState(formatTools.reduce((m, t) => ({...m, [`${t}Activated`]: document.queryCommandState(t)}), {}))
  }
}



// WEBPACK FOOTER //
// ./~/report-components/src/components/richText.js